class CategorySlider extends HTMLElement {
    constructor() {
        super();
        this.track = this.querySelector('[data-track]');
        this.dots = this.querySelectorAll('[data-dot]');
        this.slides = this.querySelectorAll('[data-slide]');
        this.slideWidth = 0;
        this.maxIndex = this.slides.length - 1;

        this.init();
    }

    init() {
        if (!this.track || this.dots.length === 0) return;

        this.setupDots();
        this.setupScroll();
        this.setupResize();
        this.updateSlideWidth();
    }

    setupDots() {
        this.dots.forEach((dot, index) => {
            dot.addEventListener('click', () => {
                this.goToSlide(index);
            });
        });
    }

    setupScroll() {
        // Use debounce or throttle if needed, but for dots update simple scroll is fine
        let isScrolling;
        this.track.addEventListener('scroll', () => {
            window.clearTimeout(isScrolling);
            isScrolling = setTimeout(() => {
                this.onScroll();
            }, 50);
        }, { passive: true });
    }

    setupResize() {
        let resizeTimer;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                this.updateSlideWidth();
                // Adjust scroll position if necessary
            }, 250);
        });
    }

    updateSlideWidth() {
        if (this.slides.length > 0) {
            const slideRect = this.slides[0].getBoundingClientRect();
            const gap = parseInt(getComputedStyle(this.track).gap) || 20;
            this.slideWidth = slideRect.width + gap;

            this.updateMaxIndex();
            this.updateDotsVisibility();
            this.onScroll(); // Update active dot based on new calculations
        }
    }

    updateMaxIndex() {
        const scrollWidth = this.track.scrollWidth;
        const clientWidth = this.track.clientWidth;
        const maxScroll = Math.max(0, scrollWidth - clientWidth);

        // Calculate max index based on possible scroll positions
        // We want to find the last index that triggers a scroll
        this.maxIndex = Math.max(0, Math.ceil(maxScroll / this.slideWidth));

        // Ensure we don't exceed slide count
        this.maxIndex = Math.min(this.maxIndex, this.slides.length - 1);
    }

    updateDotsVisibility() {
        if (!this.dots) return;
        this.dots.forEach((dot, index) => {
            if (index > this.maxIndex) {
                dot.style.display = 'none';
            } else {
                dot.style.display = '';
            }
        });
    }

    onScroll() {
        const scrollLeft = this.track.scrollLeft;
        const scrollWidth = this.track.scrollWidth;
        const clientWidth = this.track.clientWidth;

        // Check if we are at the very end (allowing for small rounding errors)
        const isAtEnd = Math.abs((scrollLeft + clientWidth) - scrollWidth) < 5;

        let newIndex;
        if (isAtEnd) {
            newIndex = this.maxIndex;
        } else {
            newIndex = Math.round(scrollLeft / this.slideWidth);
        }

        this.updateActiveDot(newIndex);
    }

    updateActiveDot(index) {
        this.dots.forEach((dot, i) => {
            if (i === index) {
                dot.classList.add('active');
                dot.setAttribute('aria-current', 'true');
            } else {
                dot.classList.remove('active');
                dot.removeAttribute('aria-current');
            }
        });
    }

    goToSlide(index) {
        const targetIndex = Math.max(0, Math.min(index, this.maxIndex));

        let scrollLeft;

        // If targeting the last visible dot, scroll to the very end
        if (targetIndex === this.maxIndex) {
            scrollLeft = this.track.scrollWidth - this.track.clientWidth;
        } else {
            scrollLeft = targetIndex * this.slideWidth;
        }

        this.track.scrollTo({
            left: scrollLeft,
            behavior: 'smooth'
        });
    }
}

customElements.define('category-slider', CategorySlider);